import { cn } from '@repo/tailwind-config/tailwind.functions'
import { CustomOption } from '@repo/toolkit/types'
import ActionButton from '@repo/ui/action-button'
import DataTable, { Column, DataTableProps, TableRequiredFields } from '@repo/ui/data-table'
import { DeleteIcon } from '@repo/ui/icons'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { EditableCell } from './editable-cell'
import { EditableTableTooltip, TooltipValueTypes } from './tooltip'

type EditableTableValue = {
  id: number
  value: number | string | undefined
}

export type EditableTableColumn<TData extends TableRequiredFields> = Column<TData> & {
  isEditable?: boolean | ((data: TData) => boolean)
  inputType?: 'number' | 'text' | 'select' | 'date'
  inputWrapperClassName?: string
  inputClassName?: string
  options?: CustomOption<string | number>[]
  optional?: boolean
  inputLabel?: string
  tooltip?: boolean
  isEditableCondition?: (data: TData) => boolean
  onValueChange?: (data: EditableTableValue) => void
  /** Optional max value for number inputs */
  max?: number
  min?: number
} & (
    | {
        tooltip: true
        initialValue?: TooltipValueTypes<TData>
        olValue?: TooltipValueTypes<TData>
      }
    | { tooltip: false }
    | { tooltip?: undefined }
  )

type ActionButtons = {
  onDelete?: (id: number) => void
}

type EditableTableProps<TData extends TableRequiredFields> = Pick<
  DataTableProps<TData, undefined>,
  | 'isLoading'
  | 'footerHeader'
  | 'footerHeaderClassName'
  | 'footer'
  | 'manualSorting'
  | 'overflow'
  | 'disableHover'
> & {
  data?: TData[]
  columns: EditableTableColumn<TData>[]
  isEditable?: boolean
  actionButtons?: ActionButtons
  footerRowClassName?: string
  onDataChange?: (data: TData[], changedData: TData[]) => void
}
const EditableTable = <TData extends TableRequiredFields>({
  columns,
  data = [],
  isEditable = false,
  actionButtons,
  overflow = true,
  footerRowClassName,
  onDataChange,
  ...props
}: EditableTableProps<TData>) => {
  const editedDataRef = useRef<TData[]>([])
  const [renderVersion, setRenderVersion] = useState(0)

  const handleValueChange = useCallback(
    <K extends keyof TData>(rowId: number, columnKey: K, value: number | string | undefined) => {
      if (isEditable) {
        const editedData = editedDataRef.current
        const updatedData = editedData.map((item) => {
          if (item.id === rowId) return { ...item, [columnKey]: value }
          return item
        })
        const changedItems = updatedData.filter(
          (item, index) => JSON.stringify(item) !== JSON.stringify(editedData[index])
        )
        editedDataRef.current = updatedData
        onDataChange?.(updatedData, changedItems)
        // Force re-render to update the display
        setRenderVersion((v) => v + 1)
      }
    },
    [editedDataRef, isEditable, onDataChange]
  )

  const tableColumns: EditableTableColumn<TData>[] = useMemo(
    () =>
      !isEditable
        ? columns.map((column) => {
            if (column.tooltip)
              return {
                ...column,
                cell: (props) => {
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  const cellContent = column.cell
                    ? typeof column.cell === 'function'
                      ? column.cell(props)
                      : column.cell
                    : props.row.original[column.accessorKey as keyof TData]
                  return (
                    <EditableTableTooltip column={column} data={props.row.original}>
                      {cellContent}
                    </EditableTableTooltip>
                  )
                }
              }
            return column
          })
        : ([
            ...columns.map((column) => {
              if (!column.isEditable) return column
              return {
                ...column,
                cell: (props) => {
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  const cellContent = column.cell
                    ? typeof column.cell === 'function'
                      ? column.cell(props)
                      : column.cell
                    : props.row.original[column.accessorKey as keyof TData]

                  return !column.isEditableCondition ||
                    column.isEditableCondition?.(props.row.original) ? (
                    <EditableTableTooltip column={column} data={props.row.original} isInput>
                      <EditableCell
                        row={props.row}
                        column={column}
                        onValueChange={handleValueChange}
                      />
                    </EditableTableTooltip>
                  ) : (
                    <EditableTableTooltip column={column} data={props.row.original}>
                      {cellContent}
                    </EditableTableTooltip>
                  )
                }
              } satisfies EditableTableColumn<TData>
            }),

            /** action button sare added only in case of editable mode */
            ...(actionButtons?.onDelete
              ? ([
                  {
                    header: '',
                    accessorKey: 'actions',
                    cell: ({ row }) => (
                      <ActionButton
                        icon={DeleteIcon}
                        variant={'danger'}
                        disabled={props.isLoading}
                        onClick={() => {
                          const id = row.original.id
                          if (id) actionButtons.onDelete?.(id)
                        }}
                      />
                    )
                  }
                ] satisfies EditableTableColumn<TData>[])
              : [])
          ] satisfies EditableTableColumn<TData>[]),
    [columns, actionButtons, isEditable, props.isLoading, handleValueChange]
  )

  // Initialize edited data when entering edit mode or when data changes
  useEffect(() => {
    if (isEditable) {
      editedDataRef.current = data
      setRenderVersion((v) => v + 1)
    }
  }, [isEditable, data])

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const displayData = useMemo(() => {
    return isEditable ? editedDataRef.current : data
  }, [isEditable, data, renderVersion])

  return (
    <DataTable
      overflow={overflow}
      headerRowClassName={'h-12'}
      headerClassName={'bg-white-active'}
      footerRowClassName={cn('bg-white-hover h-12', footerRowClassName)}
      footerCellClassName={'rounded-b-xl'}
      data={displayData}
      columns={tableColumns.map((c, index) => ({
        ...c,
        columnHeaderClassName: c.columns
          ? cn(c.columnHeaderClassName, '!rounded-none')
          : cn(c.columnHeaderClassName, {
              'rounded-tl-xl': index === 0,
              'rounded-tr-xl': index === tableColumns.length - 1
            })
      }))}
      paginated={false}
      rowCount={displayData.length}
      {...props}
    />
  )
}

export default EditableTable
